import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { isIntegerValid, isNumberValid } from 'shared/lib/validators';

// TODO: resetOnSubmit by mohlo byt default true???
const defaultOpts = {
  resetOnSubmit: false,
  scrollToError: true,
  scrollToTop: true,
  bypassValidation: false,
};
const useForm = (callback, initState = {}, validate = () => {}, opts = {}) => {
  const [submitting, setSubmitting] = useState(false);
  const [values, setValues] = useState(initState);
  const [errors, setErrors] = useState({});
  const { t } = useTranslation();

  const { resetOnSubmit, scrollToError, scrollToTop } = { ...defaultOpts, ...opts };

  const handleReset = () => {
    setValues(initState);
  };

  const _scrollToFirstError = () => {
    if (window && window.jQuery) {
      setTimeout(() => {
        try {
          const firstError = window.jQuery('.error');
          window.jQuery('html,body').animate({ scrollTop: firstError.offset().top - 10, scrollLeft: 0 }, 300);
        } catch {}
      });
    }
  };

  const _scrollToTop = () => {
    if (window && window.jQuery) {
      setTimeout(() => {
        try {
          window.jQuery('html,body').animate({ scrollTop: 0, scrollLeft: 0 }, 300);
        } catch {}
      });
    }
  };

  const setError = (name, error) => {
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
    if (scrollToError) {
      _scrollToFirstError();
    }
  };

  const handleSubmit = async (event) => {
    setSubmitting(true);
    if (event) {
      event.preventDefault();
    }

    const errs = validate ? validate(values) : {};
    setErrors(errs);

    if (_.isEmpty(errs) || opts.bypassValidation) {
      try {
        await callback(values, errs, setValues);
        if (resetOnSubmit) {
          handleReset();
        }
        if (!_.isEmpty(errs) && opts.bypassValidation && scrollToError) {
          _scrollToFirstError();
        } else if (scrollToTop) {
          _scrollToTop();
        }
      } catch (err) {
        if (_.isObject(err.response)) {
          const stateKeys = Object.keys(values);
          const responseErrors = Object.entries(err.response)
            .filter(([errKey]) => stateKeys.includes(errKey))
            .reduce((acc, [errKey, errString]) => {
              acc[errKey] = t(errString);
              return acc;
            }, {});
          setErrors(responseErrors);
          if (scrollToError) {
            _scrollToFirstError();
          }
        } else {
          console.error('Error calling useForm callback', err);
          Sentry.captureException(err);
        }
      }
    } else if (scrollToError) {
      _scrollToFirstError();
    }
    setSubmitting(false);
  };

  const setValue = (name, rawValue, type = 'text') => {
    const value = type !== 'text' && type !== 'textarea' && isNumberValid(rawValue) ? (isIntegerValid(rawValue) ? parseInt(rawValue, 10) : parseFloat(rawValue)) : rawValue;

    setValues(prevState => _.set({ ...prevState }, name, value));

    if (errors && errors[name]) {
      const { [name]: deleted, ...rest } = errors;
      setErrors(rest);
    }
  };

  const handleChange = (event) => {
    if (event.persist) {
      event.persist();
    }
    const { name, type } = event.target;
    let value = type === 'checkbox' ? event.target.checked : event.target.value;
    if (value === 'true') {
      value = true;
    } else if (value === 'false') {
      value = false;
    } else if (value === 'null') {
      value = null;
    }
    setValue(name, value, type);
  };

  const resetError = (name) => {
    const { [name]: removed, ...rest } = errors;
    setErrors(rest);
  };

  const resetErrors = (names) => {
    const errs = { ...errors };
    names.forEach((key) => {
      delete errs[key];
    });

    setErrors(errs);
  };

  const resetNestedErrors = (id, names) => {
    const errs = { ...errors };
    if (errs[id]) {
      names.forEach((key) => {
        delete errs[id][key];
      });
    }

    if (_.isEmpty(errs[id])) {
      delete errs[id];
    }

    setErrors(errs);
  };

  const resetAllErrors = () => {
    setErrors({});
  };

  return {
    submitting,
    handleChange,
    handleSubmit,
    handleReset,
    values,
    setValues,
    setValue,
    errors,
    setError,
    resetError,
    resetErrors,
    resetNestedErrors,
    resetAllErrors,
    hasError: !_.isEmpty(errors)
  };
};

export default useForm;
